import { useState } from "react";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import {
  groupErrorMessage,
  useAgreementGroupMutations,
  useAgreementGroups,
  type AgreementGroup,
} from "@/features/contractors/agreementGroups";

/**
 * Słownik grup umów ramowych — zakładanie, zmiana nazwy i usuwanie.
 *
 * Usunięcie grupy NIE kasuje umów: odpina je, więc wracają do koszyka
 * „Bez przypisanej grupy”. Rysowany tylko dla osób z uprawnieniem
 * (canManageAgreementGroups) — backend i tak sprawdza to jeszcze raz.
 */
export function AgreementGroupsManagerModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { data: groups = [], isLoading } = useAgreementGroups(open);
  const { create, update, remove } = useAgreementGroupMutations();
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [editing, setEditing] = useState<AgreementGroup | null>(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [error, setError] = useState<string | null>(null);

  const inp =
    "w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]";

  const submitNew = () => {
    const name = newName.trim();
    if (!name) return;
    setError(null);
    create.mutate(
      { name, description: newDesc.trim() },
      {
        onSuccess: () => {
          setNewName("");
          setNewDesc("");
        },
        onError: (e) => setError(groupErrorMessage(e, "Nie udało się dodać grupy.")),
      },
    );
  };

  const submitEdit = () => {
    if (!editing) return;
    const name = editName.trim();
    if (!name) return;
    setError(null);
    update.mutate(
      { id: editing.id, name, description: editDesc.trim() },
      {
        onSuccess: () => setEditing(null),
        onError: (e) => setError(groupErrorMessage(e, "Nie udało się zapisać grupy.")),
      },
    );
  };

  const confirmRemove = (g: AgreementGroup) => {
    const suffix =
      g.agreements_count > 0
        ? ` ${g.agreements_count} umów zostanie odpiętych (same umowy zostają).`
        : "";
    if (!window.confirm(`Usunąć grupę „${g.name}”?${suffix}`)) return;
    setError(null);
    remove.mutate(g.id, {
      onError: (e) => setError(groupErrorMessage(e, "Nie udało się usunąć grupy.")),
    });
  };

  return (
    <Modal open={open} onClose={onClose} title="Grupy umów ramowych" size="md">
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Grupy (np. „budowlanka kujawsko-pomorskie”, „geodeci kujawsko-pomorskie”) porządkują
        listę podwykonawców w wycenie kosztorysu. Umowę przypina się do grup na jej karcie;
        jedna umowa może być w kilku grupach. Usunięcie grupy tylko odpina umowy.
      </p>

      {error && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Nowa grupa */}
      <div className="mb-4 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
        <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Nowa grupa</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className={inp}
            placeholder="Nazwa grupy, np. budowlanka kujawsko-pomorskie"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitNew();
              }
            }}
            aria-label="Nazwa nowej grupy"
          />
          <input
            className={inp}
            placeholder="Opis (opcjonalnie)"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            aria-label="Opis nowej grupy"
          />
          <Button size="sm" onClick={submitNew} disabled={!newName.trim() || create.isPending}>
            <Plus size={14} /> Dodaj
          </Button>
        </div>
      </div>

      {/* Lista */}
      {isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Wczytywanie…</p>
      ) : groups.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Nie ma jeszcze żadnej grupy — wszystkie umowy są „Bez przypisanej grupy”.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700">
          {groups.map((g) => (
            <li key={g.id} className="px-3 py-2">
              {editing?.id === g.id ? (
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    className={inp}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        submitEdit();
                      }
                      if (e.key === "Escape") setEditing(null);
                    }}
                    aria-label="Nazwa grupy"
                    autoFocus
                  />
                  <input
                    className={inp}
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    placeholder="Opis (opcjonalnie)"
                    aria-label="Opis grupy"
                  />
                  <div className="flex gap-1">
                    <Button size="sm" onClick={submitEdit} disabled={!editName.trim() || update.isPending}>
                      Zapisz
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setEditing(null)}>
                      <X size={14} />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {g.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {g.agreements_count} umów{g.description ? ` · ${g.description}` : ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="p-1 text-gray-500 hover:text-[var(--color-primary)]"
                    title="Zmień nazwę / opis"
                    aria-label={`Edytuj grupę ${g.name}`}
                    onClick={() => {
                      setEditing(g);
                      setEditName(g.name);
                      setEditDesc(g.description ?? "");
                    }}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    className="p-1 text-gray-500 hover:text-red-600"
                    title="Usuń grupę (umowy zostają, tylko tracą przypisanie)"
                    aria-label={`Usuń grupę ${g.name}`}
                    onClick={() => confirmRemove(g)}
                    disabled={remove.isPending}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
